import { Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    Application,
    ListNotificationSettingsRequest,
    UserNotificationEventType
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    CreateOrUpdateNotificationSettingRequest,
    ListNotificationSettingsService,
    UpdateNotificationSettingService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { first } from 'rxjs/operators';
import {
    INotificationPreferencesGroup,
    NotificationPreferencesState
} from '../settings/settings.models';

@Injectable({ providedIn: 'root' })
export class GroupedNotificationPreferencesService {
    private _userNotificationPreferencesState: NotificationPreferencesState;

    constructor(
        private _listNotificationSettingsService: ListNotificationSettingsService,
        private _updateNotificationSettingService: UpdateNotificationSettingService,
        private _userService: UserService
    ) {
        SingletonServiceGuard.register('GroupedNotificationPreferencesService');
    }

    public createNotificationPreferencesUiControls(): INotificationPreferencesGroup[] {
        return [
            {
                groupName: 'Invoice Submissions',
                preferenceControls: [
                    {
                        label: 'Get notified once invoices have been processed.',
                        id: 'invoiceProcessedPreferenceControl',
                        notificationType: UserNotificationEventType.InvoiceBatchProcessed
                    }
                ]
            },
            {
                groupName: 'Fundings',
                preferenceControls: [
                    {
                        label: 'Get notified when funding has been sent.',
                        id: 'fundingsSentPreferenceControl',
                        notificationType: UserNotificationEventType.FundingFunded
                    }
                ]
            }
        ];
    }

    public async populateNotificationPreferences(): Promise<void> {
        const user = await this._userService.user$.pipe(first()).toPromise();

        const response = await this._listNotificationSettingsService.executeOnce(
            new ListNotificationSettingsRequest({
                companyKey: user.currentCompany.companyKey,
                application: Application.Dashboard
            })
        );
        this._userNotificationPreferencesState = new NotificationPreferencesState();
        this._userNotificationPreferencesState.notificationPreferencesGroups = this.createNotificationPreferencesUiControls();

        const preferenceGroups = this._userNotificationPreferencesState
            .notificationPreferencesGroups;
        preferenceGroups.forEach((preferenceGroup) => {
            preferenceGroup.preferenceControls.forEach((preferenceControl) => {
                // Seems that this can be null in the demo service.
                if (response.rows) {
                    const matchedPreference = response.rows.find(
                        (preference) =>
                            preferenceControl.notificationType ===
                            preference.userNotificationEventType
                    );
                    preferenceControl.inAppIsOn =
                        matchedPreference && matchedPreference.sendWebsiteNotification;
                }
            });
        });
    }

    public async updatePreferences(type: UserNotificationEventType, value: boolean): Promise<void> {
        const user = await this._userService.user$.pipe(first()).toPromise();
        try {
            await this._updateNotificationSettingService.executeOnce(
                new CreateOrUpdateNotificationSettingRequest({
                    userKey: user.userKey,
                    companyKey: user.currentCompany.companyKey,
                    userNotificationEventType: type,
                    sendWebsiteNotification: value
                })
            );
        } catch (error) {
            // Reset value if the endpoint fails, then re-throw.
            this._userNotificationPreferencesState.notificationPreferencesGroups.forEach(
                (preferenceGroup) => {
                    preferenceGroup.preferenceControls.forEach((preferenceControl) => {
                        if (preferenceControl.notificationType === type) {
                            preferenceControl.inAppIsOn = !preferenceControl.inAppIsOn;
                        }
                    });
                }
            );
            throw error;
        }
    }

    public get notificationPreferencesState(): NotificationPreferencesState {
        return this._userNotificationPreferencesState;
    }
}
